import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getProperties, getMaintenanceJobs, getDashboardStats } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Home, Wrench, DollarSign, Plus, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AddMaintenanceModal from '../components/modals/AddMaintenanceModal';
import { format } from 'date-fns';

export default function TenantPortal() {
  const [property, setProperty] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [propertiesData, jobsData, statsData, leasesData] = await Promise.all([
        getProperties(),
        getMaintenanceJobs(),
        getDashboardStats(),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leases`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      setProperty(propertiesData[0] || null);
      setJobs(jobsData);
      setStats(statsData);
      
      // Find active lease for current user
      if (leasesData.data && leasesData.data.length > 0 && propertiesData[0]) {
        const currentUser = JSON.parse(localStorage.getItem('user'));
        
        // Find lease that matches this property
        const activeLease = leasesData.data.find(lease => {
          const matchesUser = lease.tenant_ids?.includes(currentUser?.id);
          const matchesProperty = lease.property_id === propertiesData[0].id;
          const isActive = lease.status === 'active';
          return matchesUser && matchesProperty && isActive;
        });
        
        if (activeLease) {
          propertiesData[0].lease = activeLease;
          setProperty({...propertiesData[0]});
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div data-testid="tenant-portal" className="max-w-7xl mx-auto animate-fade-in">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Welcome to Your Home</h1>
          <p className="text-gray-600">Manage maintenance requests and view your lease details</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              <Card data-testid="rent-amount-stat">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Rent Amount</CardTitle>
                  <DollarSign className="w-5 h-5 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">${stats.rent_amount || 0}</div>
                </CardContent>
              </Card>

              <Card data-testid="maintenance-jobs-stat">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">My Maintenance Jobs</CardTitle>
                  <Wrench className="w-5 h-5 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-gray-900">{stats.my_maintenance_jobs || 0}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">Property</CardTitle>
                  <Home className="w-5 h-5 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-sm font-medium text-gray-900">{property ? 'Leased' : 'None'}</div>
                </CardContent>
              </Card>
            </div>

            {property && (
              <Card data-testid="my-property-card" className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5 text-blue-600" />
                    My Property & Lease Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Property Address */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-xs text-blue-600 font-semibold mb-1 uppercase">Your Address</p>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{property.address}</h3>
                    <p className="text-sm text-gray-700">{property.suburb}, {property.state} {property.postcode}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                      <span>{property.bedrooms} bed</span>
                      <span>•</span>
                      <span>{property.bathrooms} bath</span>
                      {property.parking > 0 && (
                        <>
                          <span>•</span>
                          <span>{property.parking} parking</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Lease Information */}
                  {property.lease && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <p className="text-xs text-green-700 font-semibold uppercase">Rent Amount</p>
                        </div>
                        <p className="text-2xl font-bold text-green-900">${property.lease.rent_amount}</p>
                        <p className="text-xs text-green-700 mt-1 capitalize">{property.lease.rent_frequency}</p>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4 text-purple-600" />
                          <p className="text-xs text-purple-700 font-semibold uppercase">Lease Period</p>
                        </div>
                        <p className="text-sm font-semibold text-purple-900">
                          {format(new Date(property.lease.start_date), 'dd MMM yyyy')}
                        </p>
                        <p className="text-xs text-purple-700 my-1">to</p>
                        <p className="text-sm font-semibold text-purple-900">
                          {format(new Date(property.lease.end_date), 'dd MMM yyyy')}
                        </p>
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-yellow-600" />
                          <p className="text-xs text-yellow-700 font-semibold uppercase">Bond Amount</p>
                        </div>
                        <p className="text-2xl font-bold text-yellow-900">${property.lease.bond_amount}</p>
                        <p className="text-xs text-yellow-700 mt-1">Security deposit</p>
                      </div>
                    </div>
                  )}

                  {!property.lease && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Lease details will appear here once finalized by your property manager.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card data-testid="maintenance-requests">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Maintenance Requests</CardTitle>
                <Button data-testid="log-maintenance-btn" size="sm" className="gap-2" onClick={() => setShowAddModal(true)}>
                  <Plus className="w-4 h-4" />
                  Log Issue
                </Button>
              </CardHeader>
              <CardContent>
                {jobs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No maintenance requests</p>
                ) : (
                  <div className="space-y-4">
                    {jobs.slice(0, 5).map((job) => (
                      <div key={job.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{job.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{job.description}</p>
                          </div>
                          <span className={`badge ${job.status === 'completed' ? 'badge-success' : job.status === 'in_progress' ? 'badge-info' : 'badge-warning'}`}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        <AddMaintenanceModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={loadData}
        />
      </div>
    </Layout>
  );
}